/**
 * Système de bilans enseignant — fin de compétence, intermédiaire, retour AAC/supervisée.
 * Chaque bilan reprend les critères REMC des compétences traitées.
 */

export const BILAN_COMPETENCES = [
  {
    id: 'C1',
    titre: 'Maîtriser le maniement du véhicule',
    sub: 'Trafic faible ou nul — les bases mécaniques doivent être intactes',
    criteres: [
      { id: 'c1_installation', label: 'Installation au poste de conduite + vérifications intérieures/extérieures' },
      { id: 'c1_demarrage', label: 'Démarrages et arrêts (y compris en côte) sans à-coups ni calage' },
      { id: 'c1_direction', label: 'Direction et trajectoire à allure lente (tenue du volant réglementaire)' },
      { id: 'c1_rapports', label: 'Utilisation de la boîte de vitesses adaptée (sans regarder le levier)' },
      { id: 'c1_manoeuvres', label: 'Marche arrière et manœuvres (créneau, bataille, épi, demi-tour)' },
    ],
  },
  {
    id: 'C2',
    titre: 'Appréhender la route en conditions normales',
    sub: 'Circulation courante — lecture de la route et respect des règles',
    criteres: [
      { id: 'c2_indices', label: 'Recherche des indices utiles et respect de la signalisation' },
      { id: 'c2_allure', label: 'Adaptation de l\'allure aux situations (limitations RESPECTÉES)' },
      { id: 'c2_voie', label: 'Choix et tenue de la voie, position sur la chaussée' },
      { id: 'c2_intersections', label: 'Franchissement des intersections et respect des priorités (stops complets)' },
      { id: 'c2_stationnement', label: 'Giratoires, arrêts et stationnements en agglomération' },
    ],
  },
  {
    id: 'C3',
    titre: 'Circuler en conditions difficiles et partager la route',
    sub: 'Situations complexes — le vrai révélateur du niveau',
    criteres: [
      { id: 'c3_depassements', label: 'Dépassements et croisements délicats (visibilité, écarts, renoncement)' },
      { id: 'c3_insertion', label: 'Insertion et sortie de voie rapide / autoroute' },
      { id: 'c3_dense', label: 'Conduite en circulation dense et en agglomération chargée' },
      { id: 'c3_vulnerables', label: 'Partage avec les usagers vulnérables (distances latérales, anticipation)' },
      { id: 'c3_degradees', label: 'Conduite en conditions dégradées (nuit, pluie, visibilité réduite)' },
    ],
  },
  {
    id: 'C4',
    titre: 'Pratiquer une conduite autonome, sûre et économique',
    sub: 'Autonomie — niveau attendu à l\'examen',
    criteres: [
      { id: 'c4_itineraire', label: 'Suivi d\'un itinéraire de manière autonome (panneaux ou GPS)' },
      { id: 'c4_anticipation', label: 'Anticipation et analyse des situations à l\'avance' },
      { id: 'c4_eco', label: 'Conduite souple et économique (frein moteur, allure stabilisée)' },
      { id: 'c4_autoeval', label: 'Capacité d\'auto-évaluation (l\'élève identifie ses propres erreurs)' },
      { id: 'c4_decision', label: 'Prises de décision autonomes et sûres (sans sollicitation de l\'enseignant)' },
    ],
  },
];

/** Habitudes typiques prises pendant la conduite accompagnée / supervisée — à cocher si observées */
export const BILAN_HABITUDES = [
  { id: 'volant', label: 'Tenue du volant non réglementaire (une main, paume, croisements)' },
  { id: 'controles', label: 'Contrôles rétros / angles morts devenus rares ou absents' },
  { id: 'allure', label: 'Allure au-dessus des limitations (« comme l\'accompagnateur »)' },
  { id: 'distances', label: 'Distances de sécurité insuffisantes (moins de 2 secondes)' },
  { id: 'position', label: 'Position sur la chaussée approximative (trop à gauche, à cheval)' },
  { id: 'clignotants', label: 'Clignotants oubliés, tardifs ou après le début de la manœuvre' },
  { id: 'stops', label: 'Stops « glissés », priorités négociées au lieu d\'être respectées' },
  { id: 'freinage', label: 'Freinages tardifs / conduite par à-coups, manque de souplesse' },
  { id: 'confiance', label: 'Sur-confiance générale, sous-estimation des risques' },
];

/**
 * Bilan Intersections — situé en fin de compétence C2, primordial dans la formation.
 * Chaque type d'intersection est évalué sur 3 critères : Identifier / Analyser / Franchir en sécurité.
 */
export const BILAN_INTERSECTIONS = [
  { code: 'PAD',   id: 'pad',         label: 'Priorité à droite (non signalée)', hint: 'En agglomération sans panneau ni feu : je cède à tout véhicule venant de droite' },
  { code: 'STOP',  id: 'stop',        label: 'STOP', hint: 'Arrêt TOTAL obligatoire, roues immobiles, au niveau de la ligne' },
  { code: 'CLP',   id: 'cedez',       label: 'Cédez-le-passage', hint: 'Ralentir, céder la priorité, s\'arrêter si nécessaire' },
  { code: 'FEUX',  id: 'feux',        label: 'Feux tricolores', hint: 'Vert = passage si dégagé · orange fixe = arrêt sauf danger · rouge = arrêt à la ligne' },
  { code: 'FLECH', id: 'fleches',     label: 'Feux à flèches directionnelles / orange clignotant', hint: 'Flèche verte = passage dans cette direction · orange clignotant = prudence + règles de priorité en vigueur' },
  { code: 'PRIO',  id: 'prioritaire', label: 'Route à caractère prioritaire', hint: 'Panneau losange jaune : je suis prioritaire mais je reste vigilant' },
  { code: 'GIR',   id: 'giratoire',   label: 'Carrefour à sens giratoire', hint: 'Cédez-le-passage à l\'anneau, choix de voie à l\'entrée, clignotant droit à la sortie' },
  { code: 'RPD',   id: 'rondpoint',   label: 'Rond-point à priorité à droite', hint: 'Rare (ex : place de l\'Étoile) : priorité à ceux qui ENTRENT' },
  { code: 'TAD',   id: 'tad',         label: 'Tourner à droite', hint: 'Serrer à droite sans monter sur le trottoir · céder aux piétons qui traversent et aux cyclistes qui remontent' },
  { code: 'TAG',   id: 'tag',         label: 'Tourner à gauche', hint: 'Se présélectionner près de l\'axe médian (ou voie de stockage) · céder aux véhicules venant en face et aux piétons' },
  { code: 'IND',   id: 'indonesienne', label: 'Croisement à l\'indonésienne', hint: 'Deux véhicules tournant à gauche face à face : croisement par l\'AVANT (indonésien) quand la configuration ou le marquage le permet, sinon par l\'arrière' },
  { code: 'T',     id: 'teintersect', label: 'Intersection en T / débouché', hint: 'Identifier qui débouche sur qui · celui qui arrive de la branche cède selon la signalisation (ou priorité à droite)' },
  { code: 'SORT',  id: 'sortie',      label: 'Sortie de parking, chemin privé, zone piétonne', hint: 'Je NE suis PAS sur une intersection classique : je cède le passage à TOUS (piétons, cycles, véhicules des deux sens)' },
  { code: 'PN',    id: 'pn',          label: 'Passage à niveau', hint: 'Arrêt absolu si feu rouge clignotant / barrières · ne jamais s\'engager sans dégagement assuré' },
  { code: 'TRAM',  id: 'tram',        label: 'Carrefour avec tramway / voies de bus', hint: 'Le tramway est prioritaire et ne peut pas s\'arrêter court · attention aux feux spécifiques et aux couloirs réservés' },
  { code: 'AGT',   id: 'agent',       label: 'Intersection réglée par un agent', hint: 'Les gestes de l\'agent PRIMENT sur les feux et les panneaux' },
];

const INTERSECTION_CRITERES = [
  { suffix: 'identifier', label: 'IDENTIFIER — reconnaître le type d\'intersection à distance (panneaux, marquage, configuration)' },
  { suffix: 'analyser', label: 'ANALYSER — déterminer qui a la priorité, chercher les indices, adapter l\'allure' },
  { suffix: 'franchir', label: 'FRANCHIR EN SÉCURITÉ — position, contrôles, décision et franchissement fluide' },
];

export const INTERSECTIONS_COMPETENCES = BILAN_INTERSECTIONS.map((t) => ({
  id: t.code,
  titre: t.label,
  sub: t.hint,
  criteres: INTERSECTION_CRITERES.map((c) => ({ id: `int_${t.id}_${c.suffix}`, label: c.label })),
}));

/**
 * RDV Préalable de départ en conduite accompagnée — remise de l'AFFI.
 * Obligatoire, 2h minimum, en présence de l'élève, de l'accompagnateur et de l'enseignant référent.
 */
export const RDV_PREALABLE_COMPETENCES = [
  {
    id: 'COND',
    titre: 'Conditions préalables réunies',
    sub: 'À vérifier AVANT le rendez-vous — sans elles, pas de RDV préalable possible',
    criteres: [
      { id: 'rdv_etg', label: 'ETG (Code de la route) obtenu depuis moins de 5 ans' },
      { id: 'rdv_heures', label: 'Formation initiale terminée : 20 h minimum en boîte manuelle (ou 13 h en boîte automatique)' },
      { id: 'rdv_livret', label: 'Étapes du livret d\'apprentissage validées' },
      { id: 'rdv_aval', label: 'Aval de l\'enseignant référent : le niveau technique est là (le quota d\'heures ne suffit pas)' },
    ],
  },
  {
    id: 'TECH',
    titre: 'Maîtrise technique SANS double commande',
    sub: 'L\'élève conduit seul aux commandes — l\'enseignant n\'intervient ni au volant ni aux pédales',
    criteres: [
      { id: 'rdv_installation', label: 'Installation, vérifications et départ en autonomie complète' },
      { id: 'rdv_maniement', label: 'Maniement fluide (allure lente, manœuvres, boîte) sans aucune intervention' },
      { id: 'rdv_circulation', label: 'Circulation et intersections franchies en autonomie et en sécurité' },
      { id: 'rdv_allure', label: 'Allure adaptée et distances de sécurité respectées spontanément' },
      { id: 'rdv_decisions', label: 'Décisions sûres sans sollicitation de l\'enseignant (dépassements, priorités, renoncements)' },
    ],
  },
  {
    id: 'ACC',
    titre: 'Formation de l\'accompagnateur',
    sub: 'Éviter le sur-guidage et les conflits — l\'accompagnateur guide, il n\'enseigne pas',
    criteres: [
      { id: 'rdv_role', label: 'Rôle compris : accompagner et sécuriser, ne PAS refaire la formation (sur-guidage = conflits)' },
      { id: 'rdv_regles', label: 'Règles AAC connues : minimum 1 AN de conduite accompagnée ET 3 000 km parcourus (les deux conditions)' },
      { id: 'rdv_vitesses', label: 'Limitations conducteur novice connues (110 au lieu de 130, 100 au lieu de 110, 80 au lieu de 90)' },
      { id: 'rdv_livret_acc', label: 'Sait utiliser le livret d\'apprentissage et noter le suivi des trajets' },
      { id: 'rdv_conditions_acc', label: 'Conditions accompagnateur vérifiées : permis B ≥ 5 ans sans interruption, accord préalable de l\'assureur, pas de condamnation pour délit routier grave' },
      { id: 'rdv_guidage', label: 'A observé la démonstration pédagogique en circulation : sait guider verbalement sans générer de panique' },
    ],
  },
  {
    id: 'AFFI',
    titre: 'Formalités de départ — remise de l\'AFFI',
    sub: 'Sans l\'AFFI signée, l\'élève n\'est PAS couvert pour conduire accompagné',
    criteres: [
      { id: 'rdv_affi', label: 'AFFI (Attestation de Fin de Formation Initiale) complétée, signée et remise' },
      { id: 'rdv_assurance', label: 'AFFI à transmettre à l\'assureur : NE PAS CONDUIRE avant le retour écrit de la lettre-avenant (extension AAC validée)' },
      { id: 'rdv_disque', label: 'Disque « conduite accompagnée » à apposer à l\'arrière du véhicule' },
      { id: 'rdv_planif', label: 'RDV pédagogique 1 000 km planifié : entre 4 et 6 mois après le RDV préalable' },
      { id: 'rdv_contrat', label: 'Contrat de formation signé, temps de questions accordé à l\'élève et à l\'accompagnateur' },
    ],
  },
];

export const RDV_PREALABLE_INFO = {
  bandeau: 'Obligatoire · 2 h minimum · en présence de l\'élève, de l\'accompagnateur et de l\'enseignant référent · en fin de formation initiale',
  utilites: [
    'Valider que l\'élève maîtrise les bases techniques SANS double commande',
    'Former l\'accompagnateur aux bonnes pratiques (éviter le sur-guidage et les conflits)',
    'Signer l\'AFFI qui autorise légalement la conduite accompagnée — sans elle, l\'élève n\'est pas couvert',
  ],
  rappel: 'Ce n\'est PAS l\'examen du permis : pas d\'inspecteur, c\'est le formateur habituel. Tout se passe dans la bonne humeur !',
  deroulement: [
    { titre: 'Partie théorique', texte: 'Rappel des règles de sécurité, obligations légales et bonnes pratiques. Vérification de l\'accompagnateur (permis ≥ 5 ans sans interruption, accord préalable de l\'assureur). Points clés du livret, rôle de l\'accompagnateur, organisation des séances, questions et signature du contrat de formation.' },
    { titre: 'Partie pratique · conduite en circulation', texte: 'L\'élève prend le volant en conditions réelles. L\'enseignant n\'intervient plus sur les commandes : il coache l\'accompagnateur in situ pour harmoniser les pratiques (éviter sur-guidage, mauvaises habitudes, conflits). L\'accompagnateur observe depuis l\'arrière et apprend à guider verbalement sans générer de panique.' },
  ],
  apres: [
    'AFFI signée → à transmettre à l\'assureur : NE PAS CONDUIRE tant que la lettre-avenant n\'est pas revenue (sans retour écrit, l\'élève n\'est pas assuré)',
    'Double règle stricte : minimum 1 AN de conduite accompagnée ET minimum 3 000 km (même si les km sont bouclés en 6 mois, il faut attendre la fin de l\'année légale pour l\'examen)',
    'RDV pédagogique « 1 000 km » à planifier entre 4 et 6 mois après le RDV préalable : retour d\'expérience et axes de progression',
  ],
  comparatif: [
    { carac: 'Moment', prealable: 'Avant de conduire seul', pedagogique: 'Pendant la conduite accompagnée' },
    { carac: 'Objectif', prealable: 'Autorisation de départ (AFFI)', pedagogique: 'Bilan des progrès et correction des dérives' },
    { carac: 'Kilométrage', prealable: '0 km en autonomie', pedagogique: 'À ~1 000 km et ~3 000 km' },
    { carac: 'Durée', prealable: '2 heures consécutives', pedagogique: 'Souvent 1 h pratique + 2 h en salle' },
  ],
  faq: [
    { q: 'Peut-on faire le RDV préalable avant d\'avoir le code ?', r: 'Non. L\'obtention de l\'examen théorique (ETG) est une condition obligatoire pour valider la fin de la formation initiale.' },
    { q: 'L\'accompagnateur peut-il être un frère ou une sœur ?', r: 'Oui, le lien de parenté n\'importe pas. Il faut simplement le permis B depuis 5 ans sans interruption et l\'accord de l\'assurance.' },
    { q: 'Que se passe-t-il si je change d\'auto-école après le RDV préalable ?', r: 'L\'AFFI reste acquise. La nouvelle auto-école demandera probablement une heure d\'évaluation pour vérifier le niveau avant de reprendre le suivi pédagogique.' },
    { q: 'Le RDV préalable est-il obligatoire en conduite supervisée ?', r: 'Oui. Le principe est identique : 2 heures requises pour lancer la phase supervisée, même si les conditions de durée diffèrent de l\'AAC (pas de minimum de temps).' },
    { q: 'Peut-on échouer au RDV préalable ?', r: 'Oui, si la sécurité n\'est pas assurée. En cas d\'échec, pas d\'AFFI : il faut reprendre des cours avant de refaire le point.' },
    { q: 'Combien d\'accompagnateurs peuvent participer ?', r: 'Généralement un seul à l\'arrière (question de place), mais un deuxième est possible si le véhicule le permet — conseillé si l\'élève conduira avec ses deux parents.' },
    { q: 'Que faire si l\'enseignant refuse de valider le RDV ?', r: 'Ce n\'est pas une punition mais une question de sécurité : il peut prescrire quelques heures supplémentaires (avec accord de l\'élève) pour renforcer les compétences fragiles avant de refaire le point.' },
    { q: 'Que faire si l\'assurance refuse l\'extension de garantie ?', r: 'Cela arrive en cas de malus ou de véhicule trop puissant (« risque aggravé »). Solutions : changer d\'assureur ou désigner un autre accompagnateur (autre parent, oncle, grand-parent) respectant les critères.' },
    { q: 'Que faire si l\'accompagnateur ne peut plus venir le jour J ?', r: 'Le RDV ne peut légalement pas avoir lieu sans accompagnateur : reporter le rendez-vous, ou venir avec un deuxième accompagnateur désigné au contrat.' },
  ],
  infractions: {
    principe: 'L\'élève ne peut pas perdre de points sur un permis qu\'il ne possède pas encore. Radar automatique → c\'est le titulaire de la carte grise qui paie et perd les points. Interception → l\'accompagnateur peut être sanctionné s\'il a manqué de vigilance. Délit grave → c\'est le juge qui tranche.',
    responsabilites: [
      { titre: 'L\'élève est le « conducteur » légal', texte: 'Art. L121-1 du Code de la route : le conducteur est celui qui a la maîtrise du véhicule. Sa responsabilité civile ou pénale peut être engagée — mais JAMAIS de retrait de points, puisqu\'il n\'a pas encore le permis.' },
      { titre: 'Radar automatique (sans interception)', texte: 'Le titulaire de la carte grise reçoit l\'avis de contravention : il paie l\'amende et perd les points, à moins de contester pour désigner la personne qui encadrait l\'élève au moment des faits.' },
      { titre: 'Contrôle de police (interception)', texte: 'L\'élève est responsable de sa conduite, mais l\'accompagnateur (superviseur) sera sanctionné — amende et points sur SON permis — s\'il a manqué à son obligation de vigilance ou laissé un comportement dangereux.' },
      { titre: 'Délit ou accident grave', texte: 'Pas de barème automatique pour les mineurs : le juge des enfants tranche au cas par cas (gravité des faits, défaut de surveillance de l\'accompagnateur, personnalité du jeune).' },
    ],
    tableau: [
      { infraction: 'Oubli du livret d\'apprentissage', amende: '11 € (si présenté sous 5 jours)', points: 'Aucun retrait', eleve: 'Aucune' },
      { infraction: 'Absence du disque « conduite accompagnée »', amende: '35 €', points: 'Aucun retrait', eleve: 'Aucune' },
      { infraction: 'Excès de vitesse < 20 km/h', amende: '68 € (hors agglo) ou 135 €', points: '−1 point', eleve: 'Inscription au livret' },
      { infraction: 'Téléphone au volant', amende: '135 €', points: '−3 points', eleve: 'Livret + dossier préfecture' },
      { infraction: 'Refus de priorité / feu rouge grillé', amende: '135 €', points: '−4 points', eleve: 'Livret + dossier préfecture' },
    ],
    delits: [
      { titre: 'Alcool 0,5 – 0,79 g/L (contravention)', texte: 'Accompagnateur : 135 €, −6 points, suspension possible (jusqu\'à 3 ans), immobilisation du véhicule, perte possible du statut d\'accompagnateur. Élève : incident transmis à la préfecture + livret. Assurance : l\'alcool est presque toujours une exclusion de garantie.' },
      { titre: 'Alcool ≥ 0,8 g/L ou refus de test (délit)', texte: 'Jusqu\'à 2 ans de prison, 4 500 € d\'amende, −6 points, suspension/annulation du permis, stage obligatoire, confiscation possible du véhicule. Élève : dossier préfecture — le juge peut interdire de se présenter à l\'examen.' },
      { titre: 'Stupéfiants (délit systématique)', texte: 'Aucun seuil de tolérance : toute présence détectable (test salivaire ou sanguin) = délit. Jusqu\'à 2 ans de prison, 4 500 €, suspension/annulation, stage obligatoire, confiscation du véhicule.' },
    ],
    consequences_eleve: [
      'Inscription de l\'erreur dans le livret d\'apprentissage',
      'Inscription des infractions au dossier du candidat en préfecture',
      'Délits très graves (stupéfiants, alcool, délit de fuite) : le juge peut interdire de se présenter à l\'examen pendant plusieurs mois ou années',
      'Assurance : en cas de faute grave avérée, franchise majorée, refus d\'indemniser les dégâts du propre véhicule, voire résiliation du contrat',
    ],
  },
  obligations: {
    source: 'Source officielle : service-public.fr · fiche F2826 (conduite accompagnée)',
    conditions: [
      'Permis B valide depuis au moins 5 ans (sans interruption)',
      'Accord ÉCRIT préalable de l\'assureur : extension de garantie pour le(s) véhicule(s) utilisé(s)',
      'Pas d\'annulation ou d\'invalidation du permis au cours des 5 dernières années',
    ],
    pendant: [
      'Être assis À L\'AVANT, à côté de l\'apprenti conducteur',
      'Durée minimale : 1 an de conduite accompagnée ET 3 000 km minimum parcourus',
      'Conduite autorisée en France uniquement — déplacements à l\'étranger interdits',
      'Varier les conditions : routes et autoroutes, jour et nuit, différentes météos',
      'Présence d\'au moins un accompagnateur obligatoire à chaque RDV pédagogique',
    ],
    vitesses: [
      '110 km/h sur autoroute limitée à 130',
      '100 km/h sur les autres sections d\'autoroute',
      '100 km/h sur routes à 2 chaussées séparées par un terre-plein',
      '80 km/h sur les autres routes',
      '50 km/h en agglomération',
    ],
    documents: [
      'Certificat d\'immatriculation (carte grise) du véhicule',
      'Demande de permis de conduire de l\'élève (papier ou dématérialisée)',
      'Permis de conduire valide de l\'accompagnateur',
    ],
    divers: [
      'AAC accessible dès 15 ans · examen pratique possible dès 17 ans',
      'Chaque RDV pédagogique dure 3 h : 1 h de conduite (avec l\'accompagnateur) + 2 h de théorie en salle',
      'Un 3e RDV pédagogique peut être organisé à la demande de l\'école, de l\'enseignant ou de l\'accompagnateur',
      'Signe distinctif « conduite accompagnée » apposé à l\'arrière GAUCHE du véhicule',
      'Bonus AAC : période probatoire réduite à 2 ans au lieu de 3 (12 points plus vite !)',
    ],
  },
  sansPedales: {
    intro: 'Contrairement à l\'enseignant, l\'accompagnateur n\'a NI pédales NI rétroviseurs additionnels. Toute la gestion passe par l\'anticipation et la voix.',
    conseils: [
      'Anticiper TÔT : donner les consignes 150-200 m à l\'avance, courtes et simples (« au rond-point, à droite »)',
      'Une seule consigne à la fois, voix calme et posée — jamais crier ni geste brusque',
      'Regarder LOIN devant en permanence : l\'accompagnateur anticipe à la place de l\'élève tant que celui-ci ne le fait pas',
      'Commencer par des trajets simples et connus (peu de trafic), monter en difficulté très progressivement',
      'Débriefer APRÈS le trajet, moteur coupé — jamais de reproches en roulant',
      'Si la tension monte : faire une pause, changer de conducteur ou de sujet — aucun conflit en circulation',
      'Vérifier soi-même les angles morts et l\'arrière avant chaque consigne de changement de direction',
    ],
    urgence: [
      'Demander d\'abord à VOIX FERME : « ralentis », « freine », « arrête-toi à droite »',
      'Frein à main (frein de parking mécanique) : tirer PROGRESSIVEMENT en tenant le bouton — jamais d\'un coup sec (risque de blocage des roues)',
      'Si nécessaire, saisir le volant par le BAS pour corriger la trajectoire — sans le tirer brutalement',
      'En dernier recours : faire passer le point mort (levier) pour couper la traction',
      'Après toute intervention : s\'arrêter dès que possible en sécurité, souffler, analyser calmement ce qui s\'est passé',
    ],
  },
};

/**
 * Les types de bilans.
 * family 'competence' : validation de fin de compétence / mi-formation / intersections
 * family 'retour' : retour de conduite accompagnée ou supervisée (avant permis blanc)
 */
export const BILAN_TYPES = [
  { id: 'fin_c1', label: 'Fin C1 · Maniement', short: 'Bilan fin de compétence C1', comps: ['C1'], family: 'competence', desc: 'critères : C1 (5)' },
  { id: 'fin_c2', label: 'Fin C2 · Circulation', short: 'Bilan fin de compétence C2', comps: ['C2'], family: 'competence', desc: 'critères : C2 (5)' },
  { id: 'intersections', label: 'Intersections · Fin C2', short: 'Bilan intersections (fin de C2)', comps: [], special: 'intersections', family: 'competence', desc: '16 situations d\'intersection × 3 critères : Identifier · Analyser · Franchir en sécurité' },
  { id: 'fin_c3', label: 'Fin C3 · Cond. difficiles', short: 'Bilan fin de compétence C3', comps: ['C3'], family: 'competence', desc: 'critères : C3 (5)' },
  { id: 'fin_c4', label: 'Fin C4 · Autonomie', short: 'Bilan fin de compétence C4', comps: ['C4'], family: 'competence', desc: 'critères : C4 (5)' },
  { id: 'intermediaire', label: 'Intermédiaire · Mi-formation', short: 'Bilan intermédiaire (C1 + C2)', comps: ['C1', 'C2'], family: 'competence', desc: 'critères : C1 + C2 (10)' },
  { id: 'rdv_prealable', label: 'RDV Préalable · AFFI', short: 'RDV préalable de départ en conduite accompagnée (AFFI)', comps: [], special: 'rdv_prealable', family: 'rdv', desc: 'conditions + maîtrise sans double commande + formation accompagnateur + formalités AFFI (20 critères)' },
  { id: 'retour_aac', label: 'Retour AAC', short: 'Bilan retour conduite accompagnée', comps: ['C1', 'C2', 'C3', 'C4'], family: 'retour', parcours: 'aac', desc: 'critères : C1 à C4 (20) + habitudes + contexte parcours' },
  { id: 'retour_supervisee', label: 'Retour Supervisée', short: 'Bilan retour conduite supervisée', comps: ['C1', 'C2', 'C3', 'C4'], family: 'retour', parcours: 'supervisee', desc: 'critères : C1 à C4 (20) + habitudes + contexte parcours' },
];

export function getBilanType(id) {
  return BILAN_TYPES.find((t) => t.id === id) || BILAN_TYPES.find((t) => t.id === 'retour_aac');
}

export function getBilanCompetences(typeId) {
  const type = getBilanType(typeId);
  if (type.special === 'intersections') return INTERSECTIONS_COMPETENCES;
  if (type.special === 'rdv_prealable') return RDV_PREALABLE_COMPETENCES;
  return BILAN_COMPETENCES.filter((c) => type.comps.includes(c.id));
}

export function getBilanTotal(typeId) {
  return getBilanCompetences(typeId).reduce((n, c) => n + c.criteres.length, 0);
}

export const BILAN_REGLES = {
  pret_pct: 80,
  pret_max_habitudes: 2,
  presque_pct: 60,
  presque_max_non_acquis: 2,
};

export const BILAN_AVIS = {
  retour: {
    pret: 'PRÊT pour le permis blanc — le niveau est là, on planifie la simulation d\'examen.',
    presque: 'PRESQUE PRÊT — quelques points à consolider avant de programmer le permis blanc.',
    pas_pret: 'PAS ENCORE PRÊT — des compétences sont à retravailler en leçon avant d\'envisager le permis blanc.',
  },
  competence: {
    pret: 'COMPÉTENCE VALIDÉE — on passe à la suite du programme de formation.',
    presque: 'PRESQUE VALIDÉE — quelques critères à consolider avant de valider définitivement.',
    pas_pret: 'NON VALIDÉE — la compétence doit être retravaillée en leçon avant de poursuivre.',
  },
  rdv: {
    pret: 'AFFI DÉLIVRABLE — toutes les conditions sont réunies : l\'attestation peut être signée et remise. Bon départ en conduite accompagnée !',
    presque: 'PRESQUE — quelques points à régler avant de signer l\'AFFI (revoir les critères non validés).',
    pas_pret: 'AFFI NON DÉLIVRABLE — conditions ou niveau technique insuffisants : on poursuit la formation initiale avant de reprogrammer le RDV.',
  },
};

export const BILAN_VERDICT_HEADLINES = {
  retour: {
    pret: 'Prêt pour le permis blanc 🎉',
    presque: 'Presque prêt — on consolide',
    pas_pret: 'Pas encore prêt — on retravaille',
  },
  competence: {
    pret: 'Compétence validée 🎉',
    presque: 'Presque validée — on consolide',
    pas_pret: 'Non validée — on retravaille',
  },
  rdv: {
    pret: 'AFFI délivrée 🎉 — départ en conduite accompagnée !',
    presque: 'Presque — derniers réglages avant l\'AFFI',
    pas_pret: 'AFFI non délivrable — on poursuit la formation',
  },
};

export function computeBilanVerdict(typeId, stats, habitudesCount) {
  const type = getBilanType(typeId);
  const total = getBilanTotal(typeId);
  const avis = BILAN_AVIS[type.family];
  const pct = total ? Math.round((stats.acquis / total) * 100) : 0;
  const habOk = type.family !== 'retour' || habitudesCount <= BILAN_REGLES.pret_max_habitudes;
  if (pct >= BILAN_REGLES.pret_pct && stats.non_acquis === 0 && habOk) {
    return { kind: 'pret', pct, text: avis.pret };
  }
  if (pct >= BILAN_REGLES.presque_pct && stats.non_acquis <= BILAN_REGLES.presque_max_non_acquis) {
    return { kind: 'presque', pct, text: avis.presque };
  }
  return { kind: 'pas_pret', pct, text: avis.pas_pret };
}

/** Fiches pédagogiques Top 10 pré-requis pertinentes pour chaque type de bilan (slugs de PREREQUIS_LIST) */
export const BILAN_FICHES = {
  fin_c1: ['mecanique', 'synchronisation', 'regard'],
  fin_c2: ['regard', 'placement', 'allure', 'intersections'],
  intersections: ['intersections'],
  fin_c3: ['depassements', 'partage', 'controles-retros'],
  fin_c4: ['allure', 'trajectoire', 'regard'],
  intermediaire: ['mecanique', 'synchronisation', 'regard', 'placement', 'intersections'],
  rdv_prealable: ['allure', 'controles-retros', 'partage'],
  retour_aac: ['controles-retros', 'allure', 'partage', 'trajectoire'],
  retour_supervisee: ['controles-retros', 'allure', 'partage', 'trajectoire'],
};

const STATE_LABELS = { acquis: 'Acquis', consolider: 'À consolider', non_acquis: 'Non acquis' };

/** Texte brut du bilan — à coller dans l'outil Codes Rousseau, un mail, etc. */
export function buildBilanTexte({ typeId, eleve, date, km, duree, rdv, grid, habitudes, verdictText, pct, stats, conseils }) {
  const type = getBilanType(typeId);
  const comps = getBilanCompetences(typeId);
  const total = getBilanTotal(typeId);
  const lines = [];
  lines.push(`═══ ${type.short.toUpperCase()} ═══`);
  lines.push(`Élève : ${eleve || '—'}`);
  lines.push(`Date : ${date || new Date().toLocaleDateString('fr-FR')}`);
  if (type.family === 'retour') {
    const ctx = [];
    if (km) ctx.push(`${km} km parcourus`);
    if (duree) ctx.push(`${duree} mois`);
    if (rdv) ctx.push(`${rdv} RDV pédagogique(s) effectué(s)`);
    if (ctx.length) lines.push(`Parcours : ${ctx.join(' · ')}`);
  }
  lines.push('');
  comps.forEach((comp) => {
    lines.push(`${comp.id} — ${comp.titre}`);
    comp.criteres.forEach((r) => {
      const st = grid[r.id];
      lines.push(`  • ${r.label} : ${st ? STATE_LABELS[st] : 'Non évalué'}`);
    });
    lines.push('');
  });
  const habChecked = BILAN_HABITUDES.filter((h) => habitudes?.[h.id]);
  if (habChecked.length) {
    lines.push('HABITUDES À CORRIGER (prises avec les accompagnateurs) :');
    habChecked.forEach((h) => lines.push(`  ✗ ${h.label}`));
    lines.push('');
  }
  lines.push(`VERDICT : ${verdictText} (${pct}% acquis — ${stats.acquis}/${total})`);
  if (conseils) {
    lines.push('');
    lines.push(`CONSEILS : ${conseils}`);
  }
  lines.push('');
  lines.push('Bilan généré sur codevoiture.fr');
  return lines.join('\n');
}

const SHORT_STATE = { consolider: 'à consolider', non_acquis: 'NON ACQUIS' };

function shortCritLabel(id, label) {
  if (id.startsWith('int_')) return id.split('_').pop(); // identifier | analyser | franchir
  const cut = label.split(/[(—·]/)[0].trim();
  return cut.length > 45 ? `${cut.slice(0, 45)}…` : cut;
}

/**
 * Version COMPACTE pour l'outil Codes Rousseau (limites de 1200 à 2500 mots selon les bilans).
 * Ne liste que les critères « à consolider » et « non acquis », le reste est résumé.
 */
export function buildBilanTexteCompact({ typeId, eleve, date, km, duree, rdv, grid, habitudes, verdictText, pct, stats, conseils }) {
  const type = getBilanType(typeId);
  const comps = getBilanCompetences(typeId);
  const total = getBilanTotal(typeId);
  const lines = [];
  lines.push(`${type.short.toUpperCase()} — ${eleve || '—'} — ${date || new Date().toLocaleDateString('fr-FR')}`);
  if (type.family === 'retour') {
    const ctx = [];
    if (km) ctx.push(`${km} km`);
    if (duree) ctx.push(`${duree} mois`);
    if (rdv) ctx.push(`${rdv} RDV pédago`);
    if (ctx.length) lines.push(`Parcours : ${ctx.join(' · ')}`);
  }
  lines.push(`VERDICT : ${verdictText} (${pct}% acquis — ${stats.acquis}/${total})`);
  comps.forEach((comp) => {
    const issues = [];
    let acquisCount = 0;
    comp.criteres.forEach((r) => {
      const st = grid[r.id];
      if (st === 'acquis') acquisCount++;
      else if (st === 'consolider' || st === 'non_acquis') issues.push(`${shortCritLabel(r.id, r.label)} (${SHORT_STATE[st]})`);
    });
    if (issues.length) {
      lines.push(`${comp.id} [${acquisCount}/${comp.criteres.length} acquis] : ${issues.join(' ; ')}`);
    }
  });
  const habChecked = BILAN_HABITUDES.filter((h) => habitudes?.[h.id]);
  if (habChecked.length) {
    lines.push(`Habitudes à corriger : ${habChecked.map((h) => h.label.split('(')[0].trim().toLowerCase()).join(' ; ')}`);
  }
  if (conseils) lines.push(`Conseils : ${conseils}`);
  return lines.join('\n');
}

export function countText(text) {
  return {
    mots: (text.trim().match(/\S+/g) || []).length,
    caracteres: text.length,
  };
}
